import { ReviewSearchParamsDto, convertReviewSearchDtoToSearchParamsArray } from '@/contracts/marketplace/reviews';
import { ReviewsPageState } from '@/context/reviews-page.state';
import {
    AiSelectedAnalysisProgress,
    ReviewsAutoReplyProgress,
    ReviewsExportProgress,
} from '@/context/reviews-page.types';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { ReviewEntity } from '@/entities/review.entity';
import { Sort } from '@/types/sort.type';
import {
    hasAnswerOrAiSensitiveFilters,
    shouldReconcileSelectionAfterAnswerOrAiStateChange,
} from '@/shared/review-selection-rules';
import { SetStateAction } from 'react';
import { areReviewFiltersEquivalent, normalizeReviewFilters } from '@/shared/review-filters-normalization';

export type ReviewsPageAction =
    | { type: 'SET_SELECTION_SESSION_ID'; value: string }
    | { type: 'SET_PICKED_REVIEWS'; value: string[] }
    | { type: 'SET_PICKED_PRODUCTS'; value: string[] }
    | { type: 'SET_PICKED_BRAND_GROUPS'; value: string[] }
    | { type: 'SET_REVIEWS_AND_TOTAL'; reviews: ReviewEntity[]; total: number }
    | { type: 'SET_PRODUCTS'; value: ProductGroupEntity[] }
    | { type: 'SET_BRAND_GROUPS'; value: BrandGroupEntity[] }
    | { type: 'SET_SORT'; value: Sort }
    | { type: 'SET_FILTERS'; value: SetStateAction<ReviewSearchParamsDto> }
    | { type: 'SET_REVIEWS_ON_PAGE'; value: number }
    | { type: 'SET_AI_ANALYSIS_PROGRESS'; value: SetStateAction<AiSelectedAnalysisProgress | null> }
    | { type: 'SET_EXPORT_PROGRESS'; value: SetStateAction<ReviewsExportProgress | null> }
    | { type: 'SET_AUTO_REPLY_PROGRESS'; value: SetStateAction<ReviewsAutoReplyProgress | null> }
    | { type: 'APPLY_REVIEW_ANSWER_JOB_DONE'; review: ReviewEntity }
    | { type: 'APPLY_REVIEW_FEED_ITEM_CHANGED'; review: ReviewEntity };

const resolveSetStateAction = <T>(current: T, value: SetStateAction<T>): T => {
    if (typeof value === 'function') {
        return (value as (currentValue: T) => T)(current);
    }

    return value;
};

const applyReviewFeedItemChanged = (state: ReviewsPageState, updatedReview: ReviewEntity): ReviewsPageState => {
    const existingReviewIdx = state.reviews.findIndex((review) => review.id === updatedReview.id);

    const shouldReconcileSelection =
        hasAnswerOrAiSensitiveFilters(state.filters) &&
        (existingReviewIdx === -1 ||
            shouldReconcileSelectionAfterAnswerOrAiStateChange({
                previousReview: state.reviews[existingReviewIdx],
                nextReview: updatedReview,
                filters: state.filters,
            }));

    if (existingReviewIdx === -1) {
        return shouldReconcileSelection
            ? {
                  ...state,
                  selectionReconcileRevision: state.selectionReconcileRevision + 1,
              }
            : state;
    }

    const nextReviews = [...state.reviews];
    nextReviews[existingReviewIdx] = updatedReview;

    return {
        ...state,
        reviews: nextReviews,
        selectionReconcileRevision: shouldReconcileSelection
            ? state.selectionReconcileRevision + 1
            : state.selectionReconcileRevision,
    };
};

export const reviewsPageReducer = (state: ReviewsPageState, action: ReviewsPageAction): ReviewsPageState => {
    switch (action.type) {
        case 'SET_SELECTION_SESSION_ID':
            return {
                ...state,
                selectionSessionId: action.value,
            };
        case 'SET_PICKED_REVIEWS':
            return {
                ...state,
                pickedReviews: action.value,
            };
        case 'SET_PICKED_PRODUCTS':
            return {
                ...state,
                pickedProducts: action.value,
            };
        case 'SET_PICKED_BRAND_GROUPS':
            return {
                ...state,
                pickedBrandGroups: action.value,
            };
        case 'SET_REVIEWS_AND_TOTAL':
            return {
                ...state,
                reviews: action.reviews,
                total: action.total,
            };
        case 'SET_PRODUCTS':
            return {
                ...state,
                products: action.value,
            };
        case 'SET_BRAND_GROUPS':
            return {
                ...state,
                brandGroups: action.value,
            };
        case 'SET_SORT':
            return {
                ...state,
                sort: action.value,
            };
        case 'SET_FILTERS': {
            const nextFilters = resolveSetStateAction(state.filters, action.value);
            if (areReviewFiltersEquivalent(state.filters, nextFilters)) {
                return state;
            }

            const normalizedFilters = normalizeReviewFilters(nextFilters);
            return {
                ...state,
                filters: normalizedFilters,
                filtersQuery: convertReviewSearchDtoToSearchParamsArray(normalizedFilters),
            };
        }
        case 'SET_REVIEWS_ON_PAGE':
            return {
                ...state,
                reviewsOnPage: action.value,
            };
        case 'SET_AI_ANALYSIS_PROGRESS':
            return {
                ...state,
                aiAnalysisProgress: resolveSetStateAction(state.aiAnalysisProgress, action.value),
            };
        case 'SET_EXPORT_PROGRESS':
            return {
                ...state,
                exportProgress: resolveSetStateAction(state.exportProgress, action.value),
            };
        case 'SET_AUTO_REPLY_PROGRESS':
            return {
                ...state,
                autoReplyProgress: resolveSetStateAction(state.autoReplyProgress, action.value),
            };
        case 'APPLY_REVIEW_ANSWER_JOB_DONE':
        case 'APPLY_REVIEW_FEED_ITEM_CHANGED':
            return applyReviewFeedItemChanged(state, action.review);
        default:
            return state;
    }
};
